import { firebaseAuth } from '@/lib/firebase';
import { signOut } from 'firebase/auth';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// ── Auth kick-out on expired / revoked session ───────

function handleAuthFailure() {
  signOut(firebaseAuth).catch(() => {});
  window.location.replace('/login');
}

// ── Token helper ─────────────────────────────────────

async function getToken(): Promise<string> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// ── Core fetch wrapper ───────────────────────────────

interface ApiOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  /** Override the auth token (e.g. impersonation token) */
  token?: string;
  /** Skip auth header (for public endpoints) */
  public?: boolean;
  /** Return raw text instead of JSON */
  asText?: boolean;
}

async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { token, public: isPublic, headers: extraHeaders, asText, ...rest } = options;

  const authHeader = isPublic
    ? {}
    : { Authorization: `Bearer ${token ?? (await getToken())}` };

  const hasBody = rest.body != null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader,
      ...extraHeaders,
    } as Record<string, string>,
  });

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) { handleAuthFailure(); }
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, error.message ?? res.statusText, error);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  if (asText) {
    return res.text() as unknown as T;
  }

  return res.json();
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Auth ─────────────────────────────────────────────

export const authApi = {
  me: () => apiFetch<Record<string, unknown>>('/v1/me'),
};

// ── Health ───────────────────────────────────────────

export const healthApi = {
  check: () => apiFetch<{ status: string; db: string; redis: string; version: string }>('/health', { public: true }),
};

// ── Admin — Tenants ───────────────────────────────────

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  plan: 'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  firebaseProjectId: string | null;
  customDomain: string | null;
  settings: Record<string, unknown>;
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantDetail extends Tenant {
  users: TenantUser[];
}

export interface TenantUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  role: string;
  isSupportAccount: boolean;
  isDefault: boolean;
  receiveAlerts: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface CreateTenantPayload {
  name: string;
  slug: string;
  plan?: string;
}

export interface ImpersonationToken {
  token: string;
  expiresAt: string;
  supportAccountEmail: string;
  note: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export const adminApi = {
  tenants: {
    list: (params?: { page?: number; limit?: number; status?: string }) => {
      const qs = new URLSearchParams();
      if (params?.page !== undefined) qs.set('page', String(params.page));
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.status) qs.set('status', params.status);
      const query = qs.toString();
      return apiFetch<PaginatedResponse<Tenant>>(`/v1/admin/tenants${query ? `?${query}` : ''}`);
    },

    get: (tenantId: string) =>
      apiFetch<TenantDetail>(`/v1/admin/tenants/${tenantId}`),

    create: (payload: CreateTenantPayload) =>
      apiFetch<Tenant>('/v1/admin/tenants', { method: 'POST', body: JSON.stringify(payload) }),

    update: (tenantId: string, payload: Partial<CreateTenantPayload & { status: string; plan: string }>) =>
      apiFetch<Tenant>(`/v1/admin/tenants/${tenantId}`, { method: 'PATCH', body: JSON.stringify(payload) }),

    impersonate: (tenantId: string) =>
      apiFetch<ImpersonationToken>(`/v1/admin/tenants/${tenantId}/impersonate`, { method: 'POST' }),

  inviteUser: (tenantId: string, data: {
    firstName: string;
    lastName: string;
    email: string;
    role?: string;
    receiveAlerts?: boolean;
  }) =>
    apiFetch<TenantUser>(`/v1/admin/tenants/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeUser: (tenantId: string, userId: string) =>
    apiFetch<void>(`/v1/admin/tenants/${tenantId}/users/${userId}`, { method: 'DELETE' }),

  setDefaultUser: (tenantId: string, userId: string) =>
    apiFetch<TenantUser>(`/v1/admin/tenants/${tenantId}/users/${userId}/set-default`, { method: 'POST' }),
  },

  auth: {
    me: () => apiFetch<Record<string, unknown>>('/v1/me'),
    updateMe: (data: { firstName?: string; lastName?: string; receiveAlerts?: boolean }) =>
      apiFetch<Record<string, unknown>>('/v1/me', { method: 'PATCH', body: JSON.stringify(data) }),
  },

  platformAdmins: {
    list: () => apiFetch<unknown[]>('/v1/admin/admins'),
    invite: (data: { firstName: string; lastName: string; email: string; receiveAlerts?: boolean }) =>
      apiFetch<unknown>('/v1/admin/admins', { method: 'POST', body: JSON.stringify(data) }),
    remove: (userId: string) =>
      apiFetch<void>(`/v1/admin/admins/${userId}`, { method: 'DELETE' }),
  },
};

// ── Phase 2: Agents ───────────────────────────────────

export type AgentStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
export type ChannelType = 'WEB_CHAT' | 'API' | 'EMAIL' | 'WHATSAPP' | 'SMS' | 'VOICE';
export type LlmProvider = 'GOOGLE_GEMINI' | 'OPENAI' | 'ANTHROPIC';
export type ModelUseCase = 'CONVERSATION' | 'ROUTING' | 'SUMMARIZATION' | 'EMBEDDING';

export interface Agent {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  status: AgentStatus;
  routingCountries: string[];
  routingSourceApps: string[];
  routingChannels: ChannelType[];
  routingPriority: number;
  defaultModelProviderId: string | null;
  createdAt: string;
  updatedAt: string;
  orchestrationConfig: OrchestrationConfig | null;
  _count?: { conversations: number };
}

export interface OrchestrationConfig {
  id: string;
  agentId: string;
  systemPrompt: string | null;
  maxTurnsBeforeEscalation: number;
}

export interface ModelProviderConfig {
  id: string;
  tenantId: string | null;
  provider: LlmProvider;
  modelName: string;
  useCase: ModelUseCase;
  costTier: string;
  latencyTier: string;
  priority: number;
  enabled: boolean;
  encryptedKey: string | null;
  createdAt: string;
}

export const agentApi = {
  list: () => apiFetch<Agent[]>('/v1/agents'),
  get: (id: string) => apiFetch<Agent>(`/v1/agents/${id}`),
  create: (data: { name: string; description?: string; routingChannels?: ChannelType[]; routingPriority?: number }) =>
    apiFetch<Agent>('/v1/agents', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description: string; status: AgentStatus; routingChannels: ChannelType[]; routingPriority: number; defaultModelProviderId: string }>) =>
    apiFetch<Agent>(`/v1/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  archive: (id: string) => apiFetch<Agent>(`/v1/agents/${id}`, { method: 'DELETE' }),
  upsertOrchestration: (id: string, data: { systemPrompt?: string; maxTurnsBeforeEscalation?: number }) =>
    apiFetch<OrchestrationConfig>(`/v1/agents/${id}/orchestration-config`, { method: 'POST', body: JSON.stringify(data) }),
};

export const modelProviderApi = {
  list: () => apiFetch<ModelProviderConfig[]>('/v1/agents/model-providers/list'),
  upsert: (data: { id?: string; provider: LlmProvider; modelName: string; useCase: ModelUseCase; priority?: number; enabled?: boolean; apiKey?: string }) =>
    apiFetch<ModelProviderConfig>('/v1/agents/model-providers', { method: 'POST', body: JSON.stringify(data) }),
  delete: (configId: string) => apiFetch<void>(`/v1/agents/model-providers/${configId}`, { method: 'DELETE' }),
};

// ── Phase 2: Conversations ────────────────────────────

export type ConversationStatus = 'OPEN' | 'WAITING_FOR_USER' | 'WAITING_FOR_AGENT' | 'RESOLVED' | 'ABANDONED';
export type SenderType = 'USER' | 'AGENT' | 'SYSTEM' | 'OPERATOR';
export type ConversationEventType =
  | 'CONVERSATION_STARTED' | 'MESSAGE_RECEIVED' | 'MODEL_REQUEST_STARTED'
  | 'MODEL_REQUEST_COMPLETED' | 'MODEL_REQUEST_FAILED' | 'MESSAGE_SENT'
  | 'HANDOFF_CREATED' | 'HANDOFF_RESOLVED' | 'CONVERSATION_RESOLVED' | 'CONVERSATION_ABANDONED';

export interface Conversation {
  id: string;
  tenantId: string;
  agentId: string | null;
  agent: { id: string; name: string } | null;
  channel: ChannelType;
  sourceApp: string;
  status: ConversationStatus;
  turnCount: number;
  externalUserId: string | null;
  rawChannelMetadata?: Record<string, Record<string, unknown>> | null;
  createdAt: string;
  updatedAt: string;
  _count?: { messages: number };
}

export interface ConversationDetail extends Conversation {
  messages: ConversationMessage[];
  events: ConversationEvent[];
  handoff: Handoff | null;
}

export interface ConversationMessage {
  id: string;
  senderType: SenderType;
  content: string;
  createdAt: string;
}

export interface ConversationEvent {
  id: string;
  eventType: ConversationEventType;
  actor: string;
  sequenceNumber: number;
  payload: Record<string, unknown> | null;
  durationMs: number | null;
  costUnits: number | null;
  createdAt: string;
}

export interface Handoff {
  id: string;
  conversationId: string;
  status: 'OPEN' | 'ASSIGNED' | 'RESOLVED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  note: string | null;
  assignedToId?: string | null;
  createdAt: string;
}

export const conversationApi = {
  list: (params?: { status?: ConversationStatus; agentId?: string; channel?: ChannelType; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.agentId) qs.set('agentId', params.agentId);
    if (params?.channel) qs.set('channel', params.channel);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return apiFetch<Conversation[]>(`/v1/conversations${q ? `?${q}` : ''}`);
  },
  get: (id: string) => apiFetch<ConversationDetail>(`/v1/conversations/${id}`),
  start: (data: { agentId?: string; initialMessage?: string; channel?: ChannelType }) =>
    apiFetch<{ conversation: Conversation; agentMessage: string | null }>('/v1/conversations', { method: 'POST', body: JSON.stringify(data) }),
  sendMessage: (id: string, content: string) =>
    apiFetch<{ agentMessage: string; handoffCreated: boolean }>(`/v1/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ content }) }),
  updateStatus: (id: string, status: ConversationStatus) =>
    apiFetch<Conversation>(`/v1/conversations/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

// ── Phase 2: Chat API Keys ────────────────────────────

export interface ChatApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  agentId: string | null;
  sourceApp: string;
  rateLimitPerMinute: number;
  usageCount: number;
  lastUsedAt: string | null;
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
}

export const chatApiKeyApi = {
  list: () => apiFetch<ChatApiKey[]>('/v1/settings/api-keys'),
  create: (data: { name: string; agentId?: string; sourceApp?: string; rateLimitPerMinute?: number }) =>
    apiFetch<{ key: string; record: ChatApiKey }>('/v1/settings/api-keys', { method: 'POST', body: JSON.stringify(data) }),
  revoke: (id: string) => apiFetch<void>(`/v1/settings/api-keys/${id}`, { method: 'DELETE' }),
};

// ── Phase 2: Handoffs ─────────────────────────────────

export const handoffApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<Handoff[]>(`/v1/handoffs${q}`);
  },
};

// ════════════════════════════════════════════════════════
// Phase 3 Types
// ════════════════════════════════════════════════════════

export interface Skill {
  id: string;
  name: string;
  description?: string;
  implementationType: 'KNOWLEDGE_RETRIEVAL' | 'EXTERNAL_API' | 'CODE' | 'PROMPT_ONLY';
  toolName?: string;
  inputSchema?: object;
  outputSchema?: object;
  skillConfig?: object;
  authRequirement: 'NONE' | 'OPTIONAL' | 'REQUIRED';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  approvalRequired: boolean;
  approvalRoles: string[];
  maxExecutionsPerConversation?: number;
  maxExecutionsPerHour?: number;
  countryRestrictions: string[];
  systemPromptInstructions?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  agentConfigs?: AgentSkillConfig[];
  _count?: { agentConfigs: number };
}

export interface AgentSkillConfig {
  id: string;
  agentId: string;
  skillId: string;
  enabled: boolean;
  approvalRequired?: boolean;
  sortOrder: number;
  skill: Skill;
  agent?: { id: string; name: string; status: string };
}

export interface Integration {
  id: string;
  name: string;
  type: 'REST_API' | 'GRAPHQL' | 'TWILIO' | 'EMAIL';
  active: boolean;
  config: {
    baseUrl?: string;
    timeout?: number;
    authType?: 'NONE' | 'BEARER' | 'API_KEY' | 'BASIC';
    authValue?: string;
    apiKeyHeader?: string;
    // Twilio settings
    accountSid?: string;
    authToken?: string;
    phoneNumber?: string;
    // Email settings
    provider?: 'sendgrid' | 'postmark';
    apiKey?: string;
    fromAddress?: string;
    inboundDomain?: string;
  };
  createdAt: string;
  _count?: { toolDefinitions: number };
}

export interface ToolDefinition {
  id: string;
  toolName: string;
  displayName: string;
  description: string;
  executorType: 'HTTP' | 'CODE' | 'DB_QUERY';
  integrationId?: string;
  integration?: Integration;
  httpConfig?: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    headers?: Record<string, string>;
  };
  sandboxCode?: string;
  inputSchema?: object;
  outputSchema?: object;
  active: boolean;
  createdAt: string;
}

export interface KnowledgeTopic {
  id: string;
  name: string;
  sourceId: string;
  parentId?: string;
  children?: KnowledgeTopic[];
  articles?: KnowledgeArticle[];
}

export interface KnowledgeSource {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;
  _count?: { articles: number; topics: number };
}

export interface KnowledgeArticle {
  id: string;
  sourceId: string;
  topicId?: string;
  title: string;
  content: string;
  country: string | null;  // ISO 3166-1 alpha-2, null = global
  language: string;        // ISO 639-1
  published: boolean;
  indexedAt?: string;
  createdAt: string;
  updatedAt: string;
  topic?: { id: string; name: string };
  _count?: { chunks: number };
}

export interface ApprovalRequest {
  id: string;
  conversationId: string;
  skillId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'TIMED_OUT';
  userMessage?: string;
  expiresAt: string;
  resolvedBy?: string;
  resolvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
}

export interface Guardrail {
  id: string;
  name: string;
  description?: string;
  ruleType: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  pattern?: string;
  agentIds: string[];
  active: boolean;
  createdAt: string;
}

// ── Phase 3: Skills ───────────────────────────────────

export const skillApi = {
  list: () => apiFetch<Skill[]>('/v1/skills'),
  get: (id: string) => apiFetch<Skill>(`/v1/skills/${id}`),
  create: (body: Partial<Skill>) => apiFetch<Skill>('/v1/skills', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Skill>) => apiFetch<Skill>(`/v1/skills/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  archive: (id: string) => apiFetch<void>(`/v1/skills/${id}`, { method: 'DELETE' }),
  agentSkills: (agentId: string) => apiFetch<AgentSkillConfig[]>(`/v1/agents/${agentId}/skills`),
  attachSkill: (agentId: string, skillId: string) =>
    apiFetch<AgentSkillConfig>(`/v1/agents/${agentId}/skills`, { method: 'POST', body: JSON.stringify({ skillId }) }),
  detachSkill: (agentId: string, skillId: string) =>
    apiFetch<void>(`/v1/agents/${agentId}/skills/${skillId}`, { method: 'DELETE' }),
};

// ── Phase 3: Tools & Integrations ────────────────────

export const integrationApi = {
  list: () => apiFetch<Integration[]>('/v1/integrations'),
  create: (body: {
    name: string;
    type: 'REST_API' | 'GRAPHQL' | 'TWILIO' | 'EMAIL';
    baseUrl?: string;
    timeout?: number;
    authType?: 'NONE' | 'BEARER' | 'API_KEY' | 'BASIC';
    authValue?: string;
    apiKeyHeader?: string;
    // Twilio/Email custom settings passed directly in config
    config?: Record<string, unknown>;
  }) =>
    apiFetch<Integration>('/v1/integrations', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<{
    name: string;
    type: 'REST_API' | 'GRAPHQL' | 'TWILIO' | 'EMAIL';
    baseUrl?: string;
    timeout?: number;
    authType?: 'NONE' | 'BEARER' | 'API_KEY' | 'BASIC';
    authValue?: string;
    apiKeyHeader?: string;
    config?: Record<string, unknown>;
  }>) =>
    apiFetch<Integration>(`/v1/integrations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/integrations/${id}`, { method: 'DELETE' }),
};

export const toolApi = {
  list: () => apiFetch<ToolDefinition[]>('/v1/tools'),
  create: (body: Partial<ToolDefinition>) => apiFetch<ToolDefinition>('/v1/tools', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: object) => apiFetch<ToolDefinition>(`/v1/tools/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/tools/${id}`, { method: 'DELETE' }),
  test: (id: string, params: Record<string, unknown>) =>
    apiFetch<unknown>(`/v1/tools/${id}/test`, { method: 'POST', body: JSON.stringify(params) }),
  audits: () => apiFetch<unknown[]>('/v1/tools/audit'),
};

// ── Phase 3: Knowledge ────────────────────────────────

export const knowledgeApi = {
  listSources: () => apiFetch<KnowledgeSource[]>('/v1/knowledge/sources'),
  getSource: (id: string) => apiFetch<KnowledgeSource>(`/v1/knowledge/sources/${id}`),
  createSource: (body: { name: string; description?: string }) =>
    apiFetch<KnowledgeSource>('/v1/knowledge/sources', { method: 'POST', body: JSON.stringify(body) }),
  updateSource: (id: string, body: object) =>
    apiFetch<KnowledgeSource>(`/v1/knowledge/sources/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  listLocales: (sourceId: string) =>
    apiFetch<Array<{ country: string | null; language: string; count: number }>>(`/v1/knowledge/sources/${sourceId}/locales`),
  listArticles: (sourceId: string, opts?: { country?: string; language?: string; published?: boolean; topicId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.country) params.set('country', opts.country);
    if (opts?.language) params.set('language', opts.language);
    if (opts?.published !== undefined) params.set('published', String(opts.published));
    if (opts?.topicId) params.set('topicId', opts.topicId);
    const q = params.toString() ? `?${params.toString()}` : '';
    return apiFetch<KnowledgeArticle[]>(`/v1/knowledge/sources/${sourceId}/articles${q}`);
  },
  createArticle: (sourceId: string, body: { title: string; content: string; topicId?: string; country?: string | null; language?: string }) =>
    apiFetch<KnowledgeArticle>(`/v1/knowledge/sources/${sourceId}/articles`, { method: 'POST', body: JSON.stringify(body) }),
  getArticle: (id: string) => apiFetch<KnowledgeArticle>(`/v1/knowledge/articles/${id}`),
  updateArticle: (id: string, body: object) =>
    apiFetch<KnowledgeArticle>(`/v1/knowledge/articles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteArticle: (id: string) => apiFetch<void>(`/v1/knowledge/articles/${id}`, { method: 'DELETE' }),
  publish: (id: string) => apiFetch<{ message: string }>(`/v1/knowledge/articles/${id}/publish`, { method: 'POST' }),
  unpublish: (id: string) => apiFetch<KnowledgeArticle>(`/v1/knowledge/articles/${id}/unpublish`, { method: 'POST' }),
  getChunks: (id: string) => apiFetch<unknown[]>(`/v1/knowledge/articles/${id}/chunks`),
  search: (body: { query: string; sourceId?: string; country?: string; language?: string; topN?: number }) =>
    apiFetch<unknown[]>('/v1/knowledge/search', { method: 'POST', body: JSON.stringify(body) }),
};

// ── Phase 3: Approvals ────────────────────────────────

export const approvalApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<ApprovalRequest[]>(`/v1/approvals${q}`);
  },
  get: (id: string) => apiFetch<ApprovalRequest>(`/v1/approvals/${id}`),
  approve: (id: string) => apiFetch<ApprovalRequest>(`/v1/approvals/${id}/approve`, { method: 'POST' }),
  reject: (id: string, reason?: string) =>
    apiFetch<ApprovalRequest>(`/v1/approvals/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
};

// ── Phase 3: Guardrails ───────────────────────────────

export const guardrailApi = {
  list: () => apiFetch<Guardrail[]>('/v1/guardrails'),
  create: (body: Partial<Guardrail>) => apiFetch<Guardrail>('/v1/guardrails', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Guardrail>) =>
    apiFetch<Guardrail>(`/v1/guardrails/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/guardrails/${id}`, { method: 'DELETE' }),
};


// ── Alerts ───────────────────────────────────────────

export interface AlertItem {
  id: string;
  tenantId: string | null;
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  status: 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';
  source: string;
  title: string;
  message: string;
  resolutionHint: string | null;
  context: Record<string, unknown>;
  resolvedAt: string | null;
  createdAt: string;
}

export const alertApi = {
  list: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return apiFetch<AlertItem[]>(`/v1/settings/alerts${q}`);
  },
  acknowledge: (id: string) => apiFetch<AlertItem>(`/v1/settings/alerts/${id}/acknowledge`, { method: 'POST' }),
  resolve: (id: string) => apiFetch<AlertItem>(`/v1/settings/alerts/${id}/resolve`, { method: 'POST' }),
};

// ════════════════════════════════════════════════════════
// Phase 4 Types & API
// ════════════════════════════════════════════════════════

// ── Workflow ──────────────────────────────────────────

export type WorkflowStatus =
  | 'PENDING' | 'RUNNING' | 'WAITING_APPROVAL'
  | 'WAITING_CALLBACK' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface WorkflowStep {
  id: string;
  name: string;
  type: 'skill' | 'tool' | 'wait' | 'condition' | 'callback';
  skillId?: string;
  toolName?: string;
  waitDurationMs?: number;
  conditionExpression?: string;
  onSuccess?: string;
  onFailure?: string;
  timeoutMs?: number;
  retries?: number;
}

export interface WorkflowDefinition {
  id: string;
  tenantId: string;
  agentId: string;
  name: string;
  description?: string;
  steps: WorkflowStep[];
  triggerConfig?: any;
  abortListeners?: any[];
  active: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  agent?: { id: string; name: string };
  executions?: WorkflowExecution[];
  _count?: { executions: number };
}

export interface WorkflowExecution {
  id: string;
  tenantId: string;
  definitionId: string;
  conversationId?: string;
  triggerExecutionId?: string;
  status: WorkflowStatus;
  currentStepIndex: number;
  state: Record<string, unknown>;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  definition?: Pick<WorkflowDefinition, 'id' | 'name' | 'steps'>;
}

export const workflowApi = {
  list: () => apiFetch<WorkflowDefinition[]>('/v1/workflows'),
  get: (id: string) => apiFetch<WorkflowDefinition>(`/v1/workflows/${id}`),
  create: (body: Partial<WorkflowDefinition>) =>
    apiFetch<WorkflowDefinition>('/v1/workflows', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<WorkflowDefinition>) =>
    apiFetch<WorkflowDefinition>(`/v1/workflows/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/workflows/${id}`, { method: 'DELETE' }),
  execute: (id: string, body?: { conversationId?: string; initialState?: Record<string, unknown> }) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/${id}/execute`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  getExecution: (executionId: string) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/executions/${executionId}`),
  cancelExecution: (executionId: string) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/executions/${executionId}/cancel`, { method: 'POST' }),
  resumeExecution: (executionId: string, data: unknown) =>
    apiFetch<WorkflowExecution>(`/v1/workflows/executions/${executionId}/callback`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),
};

// ── Trigger ───────────────────────────────────────────

export type TriggerType = 'SCHEDULED' | 'EVENT' | 'WEBHOOK';
export type TriggerExecutionStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface TriggerDefinition {
  id: string;
  tenantId: string;
  agentId: string;
  name: string;
  description?: string;
  triggerType: TriggerType;
  config: Record<string, unknown>;
  active: boolean;
  createdAt: string;
  agent?: { id: string; name: string };
  executions?: TriggerExecution[];
  _count?: { executions: number };
}

export interface TriggerExecution {
  id: string;
  triggerId: string;
  status: TriggerExecutionStatus;
  conversationId?: string;
  payload?: Record<string, unknown>;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export const triggerApi = {
  list: () => apiFetch<TriggerDefinition[]>('/v1/triggers'),
  get: (id: string) => apiFetch<TriggerDefinition>(`/v1/triggers/${id}`),
  create: (body: Partial<TriggerDefinition>) =>
    apiFetch<TriggerDefinition>('/v1/triggers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<TriggerDefinition>) =>
    apiFetch<TriggerDefinition>(`/v1/triggers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/triggers/${id}`, { method: 'DELETE' }),
  executions: (id: string) => apiFetch<TriggerExecution[]>(`/v1/triggers/${id}/executions`),
};

// ── Handoff (extended) ────────────────────────────────

export const handoffActionsApi = {
  sendMessage: (handoffId: string, content: string) =>
    apiFetch<{ id: string; content: string; createdAt: string }>(
      `/v1/handoffs/${handoffId}/message`,
      { method: 'POST', body: JSON.stringify({ content }) },
    ),
  assign: (handoffId: string, operatorId: string) =>
    apiFetch<Handoff>(`/v1/handoffs/${handoffId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ operatorId }),
    }),
  resolve: (handoffId: string, note?: string) =>
    apiFetch<Handoff>(`/v1/handoffs/${handoffId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
};

// ── Phase 5: Voice Numbers (Twilio Settings) ──────────

export interface TwilioPhoneNumber {
  id: string;
  tenantId: string;
  phoneNumber: string;
  displayName: string;
  agentId: string | null;
  language: string;
  ttsVoice: string;
  welcomeMessage: string;
  noInputMessage: string;
  handoffPhoneNumber: string | null;
  handoffMessage: string;
  maxAiTurns: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  agent?: { id: string; name: string };
}

export const voiceNumbersApi = {
  list: () => apiFetch<TwilioPhoneNumber[]>('/v1/settings/voice-numbers'),
  get: (id: string) => apiFetch<TwilioPhoneNumber>(`/v1/settings/voice-numbers/${id}`),
  create: (body: Partial<TwilioPhoneNumber>) =>
    apiFetch<TwilioPhoneNumber>('/v1/settings/voice-numbers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<TwilioPhoneNumber>) =>
    apiFetch<TwilioPhoneNumber>(`/v1/settings/voice-numbers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) => apiFetch<void>(`/v1/settings/voice-numbers/${id}`, { method: 'DELETE' }),
};

// ── Phase 6: User Memory ──────────────────────────────
export interface UserMemory {
  id: string;
  tenantId: string;
  externalUserId: string;
  memoryType: 'FACT' | 'PREFERENCE' | 'CONTEXT';
  key: string;
  value: string;
  confidence: number;
  source: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const memoryApi = {
  list: (externalUserId: string) => apiFetch<UserMemory[]>(`/v1/memory/${externalUserId}`),
  export: (externalUserId: string) => apiFetch<UserMemory[]>(`/v1/memory/${externalUserId}/export`),
  store: (externalUserId: string, data: { key: string; value: string; type: 'FACT' | 'PREFERENCE' | 'CONTEXT'; confidence?: number; expiresAt?: string }) =>
    apiFetch<UserMemory>(`/v1/memory/${externalUserId}`, { method: 'POST', body: JSON.stringify(data) }),
  deleteKey: (externalUserId: string, key: string) =>
    apiFetch<void>(`/v1/memory/${externalUserId}/${key}`, { method: 'DELETE' }),
  clearAll: (externalUserId: string) =>
    apiFetch<{ success: boolean; clearedCount: number }>(`/v1/memory/${externalUserId}`, { method: 'DELETE' }),
};

// ── Phase 6: Analytics ────────────────────────────────
export interface OverviewStats {
  totalConversations: number;
  totalMessages: number;
  resolutionRate: number;
  avgTurns: number;
  avgDurationSeconds: number;
  totalTokens: number;
}

export interface VolumeStat {
  day: string;
  channel: string;
  count: number;
}

export interface SkillStat {
  day: string;
  skillId: string;
  count: number;
}

export interface CostStat {
  day: string;
  totalTokens: number;
  estimatedCost: number;
}

export interface LatencyStats {
  p50: number;
  p95: number;
}

export interface HandoffFunnel {
  totalConversations: number;
  handoffsCount: number;
  handoffRate: number;
}

export const analyticsApi = {
  getOverview: (range?: string) => apiFetch<OverviewStats>(`/v1/analytics/overview${range ? `?range=${range}` : ''}`),
  getVolume: (range?: string) => apiFetch<VolumeStat[]>(`/v1/analytics/volume${range ? `?range=${range}` : ''}`),
  getSkills: (range?: string) => apiFetch<SkillStat[]>(`/v1/analytics/skills${range ? `?range=${range}` : ''}`),
  getCosts: (range?: string) => apiFetch<CostStat[]>(`/v1/analytics/costs${range ? `?range=${range}` : ''}`),
  getLatency: (range?: string) => apiFetch<LatencyStats>(`/v1/analytics/latency${range ? `?range=${range}` : ''}`),
  getHandoffs: (range?: string) => apiFetch<HandoffFunnel>(`/v1/analytics/handoffs${range ? `?range=${range}` : ''}`),
};

// ── Phase 6: Alerts ───────────────────────────────────
export const alertsApi = {
  list: (params?: { severity?: string; status?: string; acknowledged?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.status) qs.set('status', params.status);
    if (params?.acknowledged !== undefined) qs.set('acknowledged', String(params.acknowledged));
    const q = qs.toString();
    return apiFetch<AlertItem[]>(`/v1/alerts${q ? `?${q}` : ''}`);
  },
  acknowledge: (id: string) => apiFetch<AlertItem>(`/v1/alerts/${id}/acknowledge`, { method: 'POST' }),
  acknowledgeAll: () => apiFetch<{ count: number }>('/v1/alerts/acknowledge-all', { method: 'POST' }),
  resolve: (id: string) => apiFetch<AlertItem>(`/v1/alerts/${id}/resolve`, { method: 'POST' }),
};

// ── Phase 7: SSO, Billing & Audit Log ────────────────
export interface SsoStatus {
  enabled: boolean;
  providerType: 'saml' | 'oidc' | null;
  providerId: string | null;
  samlSsoUrl: string | null;
  oidcIssuer: string | null;
  oidcClientId: string | null;
}

export interface BillingStatus {
  plan: 'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: string;
  stripeSubscriptionId: string | null;
  billingCycleStart: string;
  billingCycleEnd: string;
  usage: {
    conversations: number;
    messages: number;
    tokens: number;
    voiceMinutes: number;
  };
  estimatedCharges: {
    conversations: number;
    messages: number;
    tokens: number;
    voiceMinutes: number;
    total: number;
  };
}

export const adminSsoApi = {
  getStatus: (tenantId: string) => apiFetch<SsoStatus>(`/v1/admin/tenants/${tenantId}/sso/status`),
  configureSaml: (tenantId: string, data: { providerId: string; samlMetadataXml: string; ssoUrl: string }) =>
    apiFetch<{ success: boolean }>(`/v1/admin/tenants/${tenantId}/sso/saml`, { method: 'POST', body: JSON.stringify(data) }),
  configureOidc: (tenantId: string, data: { providerId: string; clientId: string; clientSecret: string; issuer: string }) =>
    apiFetch<{ success: boolean }>(`/v1/admin/tenants/${tenantId}/sso/oidc`, { method: 'POST', body: JSON.stringify(data) }),
};

export const billingApi = {
  getStatus: () => apiFetch<BillingStatus>('/v1/settings/billing/status'),
  createPortalSession: () => apiFetch<{ url: string }>('/v1/settings/billing/portal', { method: 'POST' }),
};

export const auditLogApi = {
  export: (from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const q = qs.toString();
    return apiFetch<string>(`/v1/settings/audit-log/export${q ? `?${q}` : ''}`, { asText: true });
  },
  adminExport: (from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const q = qs.toString();
    return apiFetch<string>(`/v1/admin/audit-log/export${q ? `?${q}` : ''}`, { asText: true });
  },
};

// ── Super Admin ── platform-wide stats ────────────────

export interface PlatformStats {
  totalPartners: number;
  totalTenants: number;
  totalUsers: number;
  trialsActive: number;
  tenantsActive: number;
  tenantsSuspended: number;
  conversationsToday: number;
}

export const superAdminApi = {
  platformStats: () => apiFetch<PlatformStats>('/v1/admin/stats'),
};

// ── Partner Management (Super Admin) ──────────────────

export interface Partner {
  id: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'SUSPENDED';
  allowedDomain: string | null;
  autoProvision: boolean;
  tenantCount: number;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  role: 'ADMIN' | 'OPERATOR';
  isDefault: boolean;
  receiveAlerts: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PartnerTenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  createdAt: string;
}

export interface PartnerDetail extends Partner {
  users: PartnerUser[];
  tenants: PartnerTenant[];
}

export interface PartnerListResponse {
  data: Partner[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export const adminPartnersApi = {
  list: (params?: { page?: number; limit?: number; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return apiFetch<PartnerListResponse>(`/v1/admin/partners${q ? `?${q}` : ''}`);
  },

  get: (partnerId: string) =>
    apiFetch<PartnerDetail>(`/v1/admin/partners/${partnerId}`),

  create: (data: {
    name: string;
    contact: { firstName: string; lastName: string; email: string };
    allowedDomain?: string;
    autoProvision?: boolean;
  }) =>
    apiFetch<Partner>('/v1/admin/partners', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (partnerId: string, data: { name?: string; allowedDomain?: string; autoProvision?: boolean }) =>
    apiFetch<Partner>(`/v1/admin/partners/${partnerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  suspend: (partnerId: string) =>
    apiFetch<Partner>(`/v1/admin/partners/${partnerId}/suspend`, { method: 'POST' }),

  activate: (partnerId: string) =>
    apiFetch<Partner>(`/v1/admin/partners/${partnerId}/activate`, { method: 'POST' }),

  inviteUser: (partnerId: string, data: {
    email: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    role?: 'ADMIN' | 'OPERATOR';
    receiveAlerts?: boolean;
  }) =>
    apiFetch<PartnerUser>(`/v1/admin/partners/${partnerId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeUser: (partnerId: string, userId: string) =>
    apiFetch<void>(`/v1/admin/partners/${partnerId}/users/${userId}`, { method: 'DELETE' }),

  setDefaultUser: (partnerId: string, userId: string) =>
    apiFetch<PartnerUser>(`/v1/admin/partners/${partnerId}/users/${userId}/set-default`, { method: 'POST' }),
};
